import { useState } from "react";
import type { GroupInfo } from "../lib/api";
import { audio } from "../lib/audio";
import { getAttendantName, setAttendantName } from "../lib/identity";

interface Props {
  group: GroupInfo;
  onReady: () => void;
}

type Step = "intro" | "name";

/**
 * The pre-show gate, styled after the original #headphone_prompt (white card on
 * a blurred backdrop). A user tap unlocks the audio context (critical on
 * mobile/Safari); then the attendant name is collected. The heavier legacy
 * checks (adblock, speed test, browser gating) are dropped as modern-browser
 * assumptions.
 */
export function PreShow({ group, onReady }: Props): React.ReactElement {
  const [step, setStep] = useState<Step>("intro");
  const [name, setName] = useState("");
  const [testing, setTesting] = useState(false);

  const start = async () => {
    setTesting(true);
    await audio.testPlayback();
    setTesting(false);
    if (getAttendantName()) onReady();
    else setStep("name");
  };

  const submitName = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setAttendantName(trimmed);
    onReady();
  };

  return (
    <div className="headphone-prompt">
      <div className="headphone-prompt__inner">
        {step === "intro" ? (
          <>
            <p style={{ fontSize: 44, margin: "4px 0 12px" }}>🔎 + 🎧</p>
            <h2>{group.pretty_name}</h2>
            <p>You will need the correct answer to move onto the next section of the story.</p>
            <p>For the best experience, make your browser window full screen.</p>
            <p>
              If you have a video call running, wear headphones or mute yourself when characters are
              talking, so other players don't hear your sound second-hand.
            </p>
            <button className="prompt-button" onClick={() => void start()} disabled={testing}>
              {testing ? "Testing audio…" : "Okay"}
            </button>
          </>
        ) : (
          <>
            <p>
              What is your name?
              <br />
              This is shown alongside your messages.
            </p>
            <div className="name-required">
              <input
                placeholder="Your name"
                value={name}
                autoFocus
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submitName()}
              />
            </div>
            <button className="prompt-button" onClick={submitName} disabled={!name.trim()}>
              Okay
            </button>
          </>
        )}
      </div>
    </div>
  );
}
